import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Effect } from "src/app/models/component-effect";
import { PropertyEffect } from "src/app/models/component-property-effect";
import { DeterminantControllerService } from "../../../services/registration-controller/determinant-controller.service";
import { DeterminantManagerComponent } from "../../determinant-manager/determinant-manager.component";

@Component({
  selector: "determinant-triggers-field",
  templateUrl: "./determinant-triggers-field.component.html",
  providers: [DeterminantControllerService],
})
export class DeterminantTriggersFieldComponent implements OnInit {

  @Input() public showHeader: boolean = true;
  @Input() public effects: Effect[] = [];
  @Input() public determinantMapById: { [key: string]: string } = {};
  @Input() public componentHidden: any;
  @Input() public componentDisabled: any;
  @Input() public componentActivate: any;
  @Input() public componentCollapsed: any;
  @Input() public componentCollapsible: any;
  @Input() public componentName: string;
  @Input() public componentType: string;

  // Only Panel components support the Collapse effect in FormIO's UI
  private readonly COLLAPSIBLE_COMPONENT_TYPES = ["panel"];
  @Output() public showDeterminantModal = new EventEmitter();
  @Output() public saveOrUpdate = new EventEmitter();
  @Output() public updateComponentProperty = new EventEmitter();

  public serviceId: string;
  public selectedDeterminant: any;

  public isManageDetsClosed: boolean = true;

  public tempEffect: Effect;
  public tempDet: any;

  public showDrawerHeader: boolean = true;

  @ViewChild(DeterminantManagerComponent) public determinantManagerComponent: DeterminantManagerComponent;

  constructor(
    private route: ActivatedRoute,
    public determinantService: DeterminantControllerService,
  ) { }

  public ngOnInit(): void {
    if (!this.serviceId) {
      this.route.params.subscribe((params) => {
        this.serviceId = params.serviceId;
        console.log(this.serviceId);
        if (!Object.keys(this.determinantMapById).length) {
          this.getServiceDeterminantKeyName();
        }
      });
    }
    if (!this.effects) {
      this.effects = [];
    } else {
      this.effects.sort((a, b) => {
        return a.sortOrderNumber - b.sortOrderNumber;
      });
    }
  }

  public getServiceDeterminantKeyName() {
    this.determinantService.getServiceDeterminantIdAndName(this.serviceId).subscribe(
      (data) => Object.assign(this.determinantMapById, data),
    );
  }

  public showModalHeader(e) {
    console.log(e);
    this.showDrawerHeader = e;
  }

  public openBuilder(effect) {
    this.tempEffect = effect;
    this.isManageDetsClosed = false;
    this.tempDet = null;
    this.showAsFullDrawer();
  }

  public closeBuilder() {
    if (this.tempEffect && this.checkEffectDet(this.tempEffect)) {
      const index = this.effects.indexOf(this.tempEffect);
      if (index > -1) {
        this.effects.splice(index, 1);
      }
    }
    this.isManageDetsClosed = true;
    this.tempDet = null;
    this.backAsFramedDrawer();
  }

  public deleteEffect(i) {
    this.effects.splice(i, 1);
    this.effects.forEach((e, index) => {
      e.sortOrderNumber = index;
    });
    this.saveOrUpdate.emit(this.effects);
    if (!this.effects || this.effects.length < 1) {
      this.updateComponent(true, "activate");
      this.updateComponent(false, "hidden");
      this.updateComponent(false, "disabled");
    }
  }

  /**
   * One past the highest sort order in use. Numbering from the array length would collide whenever
   * the existing sort orders are not exactly 0..n-1 — closeBuilder() removes an effect without
   * reindexing, and effects loaded from the backend often carry a null sortOrderNumber.
   *
   * A null counts as 0, which is how the sort above already treats it (null - n coerces to 0 - n),
   * so the new effect is ordered after the ones already there rather than alongside them.
   */
  private nextSortOrderNumber(): number {
    return this.effects.reduce(
      (max, effect) => Math.max(max, effect.sortOrderNumber ?? 0), -1) + 1;
  }

  public createEffect() {
    const newEffect = new Effect();
    newEffect.id = "temp-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8);
    newEffect.jsonDeterminants = "";
    newEffect.sortOrderNumber = this.nextSortOrderNumber();
    const activateProp = new PropertyEffect();
    activateProp.name = "activate";
    activateProp.type = "boolean";
    activateProp.value = true;
    const showProp = new PropertyEffect();
    showProp.name = "show";
    showProp.type = "boolean";
    showProp.value = true;
    const disableProp = new PropertyEffect();
    disableProp.name = "disabled";
    disableProp.type = "boolean";
    disableProp.value = false;
    const collapsedProp = new PropertyEffect();
    collapsedProp.name = "collapsed";
    collapsedProp.type = "boolean";
    collapsedProp.value = false;
    newEffect.propertyEffects.push(activateProp);
    newEffect.propertyEffects.push(showProp);
    newEffect.propertyEffects.push(disableProp);
    newEffect.propertyEffects.push(collapsedProp);
    this.effects.push(newEffect);
  }

  public createEffectAndOpenBuilder(): void {
    const existingEmpty = this.effects.find(e => this.checkEffectDet(e));
    if (existingEmpty) {
      this.openBuilder(existingEmpty);
    } else {
      this.createEffect();
      this.openBuilder(this.effects[this.effects.length - 1]);
    }
  }

  public showAsFullDrawer() {
    const windowParent = window.parent.document.querySelector(".component-settings")?.querySelector(".formio-dialog-content > div > div");
    if (windowParent) {
      windowParent.querySelectorAll("iframe")?.forEach(iframe => iframe.classList.add("full-drawer"));
      windowParent.querySelector(".page-iframe")?.classList.add("full-drawer-content");
      windowParent.querySelector(":scope > .row:nth-child(1)")?.classList.add("d-none");
      // windowParent.querySelector(":scope > .row:nth-child(2)")?.classList.add("d-none");
      windowParent.querySelector(":scope > .row:nth-child(2)")?.querySelector("#myBuilder").classList.add("m-0");
      windowParent.querySelector(":scope > .row:nth-child(2)")?.querySelector(".tab-pane.active").classList.add("p-0", "m-0");
      // windowParent.querySelector(":scope > .row:last-child")?.classList.add("d-none");
      window.parent.document.querySelector(".component-settings")?.querySelector(".formio-dialog-close")?.classList.add("d-none");
      windowParent.querySelector("#element1")?.querySelector(":scope > .formio-component-tabs")?.querySelector(":scope > div > .nav-tabs")?.classList.add("d-none");
    }
  }

  public backAsFramedDrawer() {
    const windowParent = window.parent.document.querySelector(".component-settings")?.querySelector(".formio-dialog-content > div > div");
    if (windowParent) {
      windowParent.querySelectorAll("iframe")?.forEach(iframe => iframe.classList.remove("full-drawer"));
      windowParent.querySelector(".page-iframe")?.classList.remove("full-drawer-content");
      windowParent.querySelector(":scope > .row:nth-child(1)")?.classList.remove("d-none");
      // windowParent.querySelector(":scope > .row:nth-child(2)")?.classList.remove("d-none");
      windowParent.querySelector(":scope > .row:nth-child(2)")?.querySelector("#myBuilder").classList.remove("m-0");
      windowParent.querySelector(":scope > .row:nth-child(2)")?.querySelector(".tab-pane.active")?.classList.remove("p-0", "m-0");
      // windowParent.querySelector(":scope > .row:last-child")?.classList.remove("d-none");
      window.parent.document.querySelector(".component-settings")?.querySelector(".formio-dialog-close")?.classList.remove("d-none");
      windowParent.querySelector("#element1")?.querySelector(":scope > .formio-component-tabs")?.querySelector(":scope > div >.nav-tabs")?.classList.remove("d-none");
    }
  }

  public checkDefaults(effect) {
    let activateStatus = false;
    let hiddenStatus = false;
    let disabledStatus = false;
    let collapsedStatus = false;
    if (String(this.getDefaultProperty("activate", true)) === String(this.getPropertyValue(effect, "activate", true))) {
      activateStatus = true;
    }
    if (String(this.getDefaultProperty("hidden", false)) !== String(this.getPropertyValue(effect, "show", true))) {
      hiddenStatus = true;
    }
    if (String(this.getDefaultProperty("disabled", false)) === String(this.getPropertyValue(effect, "disabled", false))) {
      disabledStatus = true;
    }
    if (String(this.getDefaultProperty("collapsed", false)) === String(this.getPropertyValue(effect, "collapsed", false))) {
      collapsedStatus = true;
    }
    return (activateStatus && String(this.getPropertyValue(effect, "activate", true)) === "false") || (activateStatus && hiddenStatus && disabledStatus && collapsedStatus);
  }

  public checkDuplicateEffects(effect) {
    const effActive = this.getPropertyValue(effect, "activate", true);
    const effShow = this.getPropertyValue(effect, "show", true);
    const effDisabled = this.getPropertyValue(effect, "disabled", false);
    const effCollapsed = this.getPropertyValue(effect, "collapsed", false);
    return this.effects.find((e) => {
      if (e.id === effect.id || this.checkEffectDet(e) || this.checkEffectDet(effect)) {
        return false;
      }
      const sameBaseValues =
        effActive === this.getPropertyValue(e, "activate", true) &&
        effShow === this.getPropertyValue(e, "show", true) &&
        effDisabled === this.getPropertyValue(e, "disabled", false);
      if (!sameBaseValues) {
        return false;
      }
      // For components that support expand/collapse, the collapsed property
      // is an independent effect dimension. Two effects are only duplicates
      // when they agree on both the base properties AND the collapse state.
      // Effects that explicitly carry a collapsed property target the
      // collapse dimension; those without it target only visibility.
      // Mixing the two types is not a duplicate even when base values match.
      if (this.showCollapsedEffect()) {
        const sameCollapsed = effCollapsed === this.getPropertyValue(e, "collapsed", false);
        if (!sameCollapsed) {
          return false;
        }
        // If one effect explicitly declares a collapsed property and the
        // other does not, they target different dimensions.
        const effHasCollapsed = this.hasPropertyEffect(effect, "collapsed");
        const otherHasCollapsed = this.hasPropertyEffect(e, "collapsed");
        if (effHasCollapsed !== otherHasCollapsed) {
          return false;
        }
      }
      return true;
    });
  }

  /**
   * Returns true when the given effect explicitly contains a propertyEffect
   * entry with the specified name, regardless of its value.
   */
  private hasPropertyEffect(effect, name: string): boolean {
    return !!effect?.propertyEffects?.find((p) => p.name === name);
  }

  public getPropertyValue(effect, prop, d) {
    let result = d;
    const propEffect = effect?.propertyEffects?.find((p) => {
      return p.name === prop;
    });
    if (propEffect) {
      if (propEffect.value === "true") {
        result = true;
      } else if (propEffect.value === "false") {
        result = false;
      } else {
        result = propEffect.value;
      }
    }
    return result;
  }

  public stringToBoolean(value, d) {
    if (value === null || value === undefined || value === "undefined") {
      return d;
    }
    if (value === true || value === "true") {
      return true;
    } else {
      return false;
    }
  }

  public getDefaultProperty(prop, d) {
    let result = d;
    if (prop === "hidden") {
      // this.componentHidden || d;
      result = this.stringToBoolean(this.componentHidden, d);
    }
    if (prop === "disabled") {
      // this.componentDisabled || d;
      result = this.stringToBoolean(this.componentDisabled, d);
    }
    if (prop === "activate") {
      // this.componentActivate || d;
      result = this.stringToBoolean(this.componentActivate, d);
    }
    if (prop === "collapsed") {
      result = this.stringToBoolean(this.componentCollapsed, d);
    }
    return result;
  }

  public updateDeterminant(eff) {
    if (this.tempDet) {
      console.log("updating determinant", eff);
      this.tempEffect.jsonDeterminants = this.tempDet;
      this.effects.forEach((e) => {
        if (e.id === this.tempEffect.id) {
          Object.assign(e, this.tempEffect);
        }
      });
      this.saveOrUpdate.emit(this.effects);
      if ((!this.effects || (this.effects.length < 1 || (this.effects.length === 1 && this.checkDefaults(this.effects[0]))) && !this.checkUnsetDet())) {
        this.updateComponent(false, "activate");
      }
    }
    this.closeBuilder();
  }

  public temoupdateDeterminant(eff) {
    console.log("temp updating determinant", eff);
    this.tempDet = eff.jsonString;
  }

  public checkEffectDet(effect) {
    return effect.jsonDeterminants === "[{\"type\":\"OR\",\"items\":[]}]" || effect.jsonDeterminants === "" || !effect.jsonDeterminants;
  }

  public checkUnsetDet() {
    return this.effects.find((e) => {
      return e.jsonDeterminants === "[{\"type\":\"OR\",\"items\":[]}]" || e.jsonDeterminants === "" || !e.jsonDeterminants;
    });
  }

  public checkDuplicateDets(eff) {
    const duplicates = this.effects.filter((e) => {
      return e.jsonDeterminants === eff.jsonDeterminants && e.id !== eff.id;
    });
    if (duplicates.length) {
      return true;
    } else {
      return false;
    }
  }

  public updateEffectProperty(e, prop, effect) {
    console.log("changing effect property", e, prop, effect);

    // Helpers
    const isTrue = (v) => v === true || v === "true";
    const isFalse = (v) => v === false || v === "false";
    const setOrAdd = (name: string, value: any) => {
      const p = effect.propertyEffects.find((pe) => pe.name === name);
      if (p) {
        p.value = value;
      } else {
        const np = new PropertyEffect();
        np.name = name;
        np.type = "boolean";
        np.value = value;
        effect.propertyEffects.push(np);
      }
    };

    setOrAdd(prop, e);

    // Mirror component-level coupling at effect level
    if (prop === "activate" && isFalse(e)) {
      setOrAdd("show", true);
      setOrAdd("disabled", false);
    }
    if (prop === "show" && isTrue(e)) {
      setOrAdd("disabled", false);
    }

    this.saveOrUpdate.emit(this.effects);
  }

  public updateComponent(e, prop) {
    // console.log("-- updateComponent", e, prop);
    if (prop === "hidden") {
      this.componentHidden = String(e);
      if (this.componentHidden === "false") {
        this.updateComponent(false, "disabled");
      }
    }
    if (prop === "disabled") {
      this.componentDisabled = String(e);
    }
    if (prop === "activate") {
      this.componentActivate = String(e);
      if (this.componentActivate === "false") {
        this.updateComponent(false, "hidden");
        this.updateComponent(false, "disabled");
      }
    }
    this.updateComponentProperty.emit({ property: prop, value: e });
  }

  public isDeterminantEmpty(jsonDeterminants: any): boolean {
    // console.log("isDeterminantEmpty", jsonDeterminants, JSON.parse(jsonDeterminants));
    if (jsonDeterminants) {
      let dets;
      if (jsonDeterminants instanceof Array || typeof jsonDeterminants === "object") {
        dets = jsonDeterminants[0]?.items;
      } else {
        dets = JSON.parse(jsonDeterminants)[0]?.items;
      }
      if (dets && dets.length > 0) {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
    return true;
  }

  /**
   * Returns true if the current component supports the Collapse effect.
   * Only Panel (Block) components support collapse in FormIO, so the toggle
   * is restricted to panel types. The componentCollapsible flag (collapsibleDS)
   * is used as a secondary signal: when componentType is not available
   * (e.g. not passed through the iframe URL), we fall back to
   * componentCollapsible which is only defined in the panel's editForm.
   */
  public showCollapsedEffect(): boolean {
    if (this.componentType) {
      return this.COLLAPSIBLE_COMPONENT_TYPES.includes(this.componentType.toLowerCase());
    }
    // Fallback: collapsibleDS is only defined in tab-general-panel.json,
    // so it can only be "true" for panel components.
    return this.componentCollapsible === true || this.componentCollapsible === "true";
  }

}
